/**
 * useGoogleAccountGuard — rejects a Google sign-in that isn't linked to any
 * Teddy Kids staff record.
 *
 * Why this exists: password sign-up is domain-restricted (@teddykids.nl only),
 * but "Continue with Google" is not — Supabase mints a real session for ANY
 * Google account. RLS denies that stranger every row, but they'd still land on
 * an authenticated (empty) app shell. This guard closes that shell-bypass.
 *
 * The guard is staff-resolution-based, NOT domain-based: staff legitimately use
 * personal gmail/live.nl addresses, and identity is resolved server-side via
 * get_my_staff (staff_emails ∪ staff_master.email) — provider-agnostic.
 *
 * Blast radius / placement:
 *  - Lives BELOW <QueryClientProvider> (mounted via GuardedLayout in App.tsx),
 *    because it uses react-query. `useAuth` and App's top-level call site both
 *    sit ABOVE that provider, so the guard cannot live there.
 *  - Password and multi-identity (google+email) sessions short-circuit on the
 *    synchronous `identities` check — no fetch. The get_my_staff query is
 *    `enabled` ONLY for google-only sessions, so password users incur ZERO
 *    extra network calls and their behaviour is completely unchanged.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { UserIdentity } from "@supabase/supabase-js";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const UNLINKED_GOOGLE_MESSAGE =
  "This Google account isn't linked to a Teddy Kids account. " +
  "Ask an admin to add your email, or sign in with your email and password.";

type IdentityLike = Pick<UserIdentity, "provider">;

/**
 * True when a session's ONLY identity/identities are Google — i.e. there is no
 * linked 'email' (password) identity. A pre-existing password user has an
 * 'email' identity; an auto-linked user has BOTH 'email' and 'google'. Only a
 * pure-Google session (a stranger who clicked "Continue with Google" with no
 * prior Teddy Kids account) qualifies.
 */
export function isGoogleOnlyIdentity(
  identities: IdentityLike[] | null | undefined,
): boolean {
  if (!identities || identities.length === 0) return false;
  return identities.every((i) => i.provider === "google");
}

/**
 * Pure decision for the guard. `staffResolved`:
 *   true  → a staff record was resolved                    → allow
 *   false → query settled with NO staff record             → reject
 *   null  → not resolved yet / still loading               → never reject (wait)
 *
 * Only google-only sessions are ever rejected; everything else is allowed and
 * never even consults `staffResolved`.
 */
export function shouldRejectGoogleOnlySession(
  identities: IdentityLike[] | null | undefined,
  staffResolved: boolean | null,
): boolean {
  if (!isGoogleOnlyIdentity(identities)) return false;
  return staffResolved === false;
}

export interface GoogleAccountGuardState {
  /**
   * True while a google-only session is being verified, and held true through
   * its rejection/sign-out — so the caller shows a loader instead of flashing
   * the app then ejecting. Always false for password / multi-identity sessions.
   */
  checking: boolean;
  /**
   * Set when the ejection sign-out itself failed — the stranger still holds a
   * live session, so the caller must keep blocking (checking stays true) AND
   * say so, never spin.
   */
  ejectError: Error | null;
}

export function useGoogleAccountGuard(): GoogleAccountGuardState {
  const { user, signOut } = useAuth();
  const identities = user?.identities;
  const googleOnly = isGoogleOnlyIdentity(identities);

  // get_my_staff runs ONLY for google-only sessions. Password / multi-identity
  // sessions keep this disabled → no extra RPC, behaviour unchanged.
  const { data: staffRows, isLoading, isError } = useQuery({
    queryKey: ["googleAccountGuard", "get_my_staff", user?.id ?? null],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_my_staff");
      if (error) throw error; // surface — never silent-null
      return data ?? [];
    },
    enabled: googleOnly,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // null = still resolving (retries included); otherwise settled → does a staff
  // record exist? A settled query error counts as "no record" → fail closed.
  const staffResolved: boolean | null = !googleOnly
    ? true
    : isLoading
      ? null
      : isError
        ? false
        : (staffRows?.length ?? 0) > 0;

  const reject = shouldRejectGoogleOnlySession(identities, staffResolved);

  const ejectedRef = useRef(false);
  const [ejectError, setEjectError] = useState<Error | null>(null);
  useEffect(() => {
    if (!reject || ejectedRef.current) return;
    ejectedRef.current = true;
    // Eject the stranger. signOut → onAuthStateChange(SIGNED_OUT) → App flips to
    // the public route tree → they land on /auth. Toast tells them what to do.
    // The toast only fires on success — it must never claim the ejection
    // happened when it did not. ejectedRef stays the re-entry guard, which
    // guards re-entry; signOut is useCallback-stable so the dep is inert.
    void signOut()
      .then(() => toast.error(UNLINKED_GOOGLE_MESSAGE))
      .catch((err: unknown) => setEjectError(err instanceof Error ? err : new Error(String(err))));
  }, [reject, signOut]);

  // Block through both the verification AND the reject/sign-out so the
  // authenticated app never flashes for a stranger. `reject` implies google-only.
  // checking deliberately stays true on failure — the shell must never render
  // for a rejected stranger.
  return { checking: googleOnly && (isLoading || reject), ejectError };
}
